/**
 * Add-ons: the screens that sit beside the two products.
 *
 * Claripick is two products. Compliance (plan 'starter') is free for every
 * NPO: the statutory register, deadlines and reminders, the document vault
 * and the office-bearers DSD must be told about. Grant management (plan
 * 'small') is the paid core: grants, the funders who give them and the
 * contract reader. Everything else is an add-on, off until somebody switches
 * it on, so a new workspace opens on a short menu.
 *
 * Grant makers (plan 'fund', orgKind 'fund') is the other side: the product
 * for the foundations, trusts, CSI teams and donors that give grants. Its
 * screens are the grantmaking ones, and it never shows an NPO's Funders
 * register or the contract reader (givesGrantsOnly below).
 *
 * Accounting is not an add-on and is not offered anywhere for now.
 *
 * Where an add-on's switch lives:
 *
 *   `org.addons`       What an administrator switched on or off, through the
 *                      setAddon callable. Absent on every workspace opened
 *                      before add-ons existed, which is how one is told from
 *                      a new one: a workspace without the field keeps every
 *                      screen its modules allow, exactly as before.
 *   `org.addonGrants`  What the founder switched on for a workspace, by hand
 *                      with the Admin SDK (scripts/grant-addon.ts). No client
 *                      can write it (firestore.rules). It holds after a trial
 *                      ends and on every plan, and the plan changes keep the
 *                      module it needs.
 *
 * An administrator may switch an add-on on themself while the workspace is
 * on a trial, Pro or the Fund plan (each of which carries the module the
 * add-on needs), and governance on any plan. On Compliance or Grant
 * management after the trial, the rest are the founder's to switch on.
 */
import { orgKindOf } from './modules.js';
import { PLANS } from './plans.js';
export const ADDON_KEYS = [
    'pipeline',
    'reports',
    'answers',
    'readiness',
    'donors',
    'cashflow',
    'outcomes',
    'governance',
];
export const ADDONS = {
    pipeline: {
        key: 'pipeline',
        label: 'Pipeline board',
        does: 'Your applications as cards on a board by stage, so you can see what is being written, what is with the funder and what came back.',
        module: 'seeker',
        freeOnEveryPlan: false,
        routes: ['pipeline'],
    },
    reports: {
        key: 'reports',
        label: 'Report builder',
        does: 'Builds the report a funder asks for from the grant’s own figures, budget against actual, with a draft narrative you edit.',
        module: 'seeker',
        freeOnEveryPlan: false,
        routes: ['reports'],
    },
    answers: {
        key: 'answers',
        label: 'Answer library',
        does: 'The paragraphs every application asks for, kept with their word counts, so the next form takes an hour and not a week.',
        module: 'seeker',
        freeOnEveryPlan: false,
        routes: ['answers'],
    },
    readiness: {
        key: 'readiness',
        label: 'Funder readiness',
        does: 'Checks your papers the way a funder will, and prints the application pack and the SED letter they ask for.',
        module: 'seeker',
        freeOnEveryPlan: false,
        routes: ['fundability', 'funder-pack', 'sed-letter'],
    },
    donors: {
        key: 'donors',
        label: 'Donors and 18A receipts',
        does: 'Donor records with consent, section 18A receipts SARS will accept, an annual receipt per donor and the IT3(d) schedule.',
        module: 'fundraising',
        freeOnEveryPlan: false,
        routes: ['fundraising'],
    },
    cashflow: {
        key: 'cashflow',
        label: 'Cash flow and restricted funds',
        does: 'How many months your reserves cover, and how much of each restricted grant is left to spend before its end date.',
        module: 'finance',
        freeOnEveryPlan: false,
        routes: ['cash-flow', 'funds'],
    },
    outcomes: {
        key: 'outcomes',
        label: 'Outcomes (M&E)',
        does: 'Indicators with baselines and targets, and the actuals your programme staff capture, ready for a funder’s report.',
        module: 'me',
        freeOnEveryPlan: false,
        routes: ['outcomes'],
    },
    governance: {
        key: 'governance',
        label: 'Governance and board pack',
        does: 'A risk register, policies with review dates on the calendar, a resolutions log, and a board pack printed in one click.',
        module: null,
        freeOnEveryPlan: true,
        routes: ['governance', 'board-pack'],
    },
};
/** The add-on whose screens a workspace route belongs to, if any. */
export function addonForRoute(route) {
    const first = route.replace(/^\/+/, '').split(/[/?#]/)[0] ?? '';
    return ADDON_KEYS.find((k) => ADDONS[k].routes.includes(first)) ?? null;
}
/** A workspace opened before add-ons existed: it has no `addons` field at all. */
export function isLegacyAddonShape(org) {
    return !org || org.addons === undefined || org.addons === null || typeof org.addons !== 'object';
}
/**
 * A grant maker's own workspace: its screens are the giving side (calls,
 * grantees, the board, payments, evidence, the SED register) and none of the
 * NPO's grant management, so no Funders register and no contract reader.
 *
 * A workspace opened since add-ons existed is told by its kind: a fund that
 * subscribes takes the Fund plan's modules, which carry the seeker toolkit
 * too, and that must not turn its menu into an NPO's. One opened before
 * add-ons keeps the test it was always drawn by (grantmaking on, seeking
 * off), so a fund that bought the plan back then keeps every screen it had.
 */
export function givesGrantsOnly(org) {
    if (!org)
        return false;
    if (isLegacyAddonShape(org))
        return org.modules?.grantmaker === true && org.modules?.seeker !== true;
    return orgKindOf(org) === 'fund';
}
function planOf(org) {
    const p = (org.effectivePlan ?? org.plan ?? 'starter');
    return p in PLANS ? p : 'starter';
}
/**
 * May an administrator switch this add-on on themself right now?
 *
 * On a trial (whose effective plan is the trialled tier), Pro or the Fund
 * plan, when that plan carries the module the add-on needs; and governance
 * on every plan. Not on Compliance or Grant management after the trial:
 * there an add-on is the founder's to switch on.
 */
export function addonSelfServe(org, key) {
    const def = ADDONS[key];
    if (def.freeOnEveryPlan)
        return true;
    const plan = PLANS[planOf(org)];
    if (plan.id !== 'pro' && plan.id !== 'fund')
        return false;
    return def.module === null || plan.modules[def.module] === true;
}
/** Whether this add-on is on for this workspace, and why. */
export function addonStatus(org, key) {
    if (!org)
        return { on: false, why: 'ask' };
    const def = ADDONS[key];
    const moduleOn = def.module === null || org.modules?.[def.module] === true;
    if (!moduleOn)
        return { on: false, why: 'module' };
    if (org.addonGrants?.[key] === true)
        return { on: true, why: 'granted' };
    // A workspace from before add-ons keeps every screen its modules allow.
    if (isLegacyAddonShape(org))
        return { on: true, why: 'legacy' };
    const self = addonSelfServe(org, key);
    if (org.addons?.[key] === true && self)
        return { on: true, why: 'chosen' };
    return { on: false, why: self ? 'off' : 'ask' };
}
export function addonOn(org, key) {
    return addonStatus(org, key).on;
}
/** The add-ons that are on, in registry order. */
export function addonsOn(org) {
    return ADDON_KEYS.filter((k) => addonOn(org, k));
}
/**
 * The decision setAddon makes on the server, and the Add-ons page mirrors.
 * Switching off is refused only where there is nothing an administrator's
 * switch can change: a legacy workspace (no switches), or one the founder
 * granted (the grant decides). Switching on also needs the module and a
 * plan that lets the administrator choose.
 */
export function addonSwitchDecision(org, key, on) {
    if (isLegacyAddonShape(org))
        return { ok: false, reason: 'legacy' };
    if (org.addonGrants?.[key] === true)
        return { ok: false, reason: 'granted' };
    if (!on)
        return { ok: true };
    const def = ADDONS[key];
    if (def.module !== null && org.modules?.[def.module] !== true)
        return { ok: false, reason: 'module' };
    if (!addonSelfServe(org, key))
        return { ok: false, reason: 'ask' };
    return { ok: true };
}
/**
 * The modules a plan change leaves a workspace with: the new set, plus the
 * module every add-on the founder granted needs. A trial ending or a
 * cancellation running out used to set the modules to the free plan's, which
 * would take a granted add-on's screens with it.
 */
export function withGrantedAddonModules(modules, addonGrants) {
    const out = { ...modules };
    for (const k of ADDON_KEYS) {
        const mod = ADDONS[k].module;
        if (mod && addonGrants?.[k] === true)
            out[mod] = true;
    }
    return out;
}
